
import React from 'react';
import { useAsset } from '../hooks/useAsset';
import { toggleFullscreen, isFullscreen, onFullscreenChange } from '../services/fullscreen';
import { useGameSettings } from '../hooks/useGameSettings';
import { Language, t } from '../data/translations';
import { useShellLayout } from '../hooks/useShellLayout';

interface OptionsScreenProps {
  onBack: () => void;
  language?: Language;
}

const OptionsScreen: React.FC<OptionsScreenProps> = ({ onBack, language = 'en' }) => {
  const [isFs, setIsFs] = React.useState(isFullscreen);
  const { settings, updateSetting } = useGameSettings();
  const { isMobileShell } = useShellLayout();

  React.useEffect(() => {
    const cleanup = onFullscreenChange(setIsFs);
    return cleanup;
  }, []);

  const handleFullscreenToggle = async () => {
    await toggleFullscreen();
  };

  const menuBg = useAsset('ui.menu.bg');
  const btnPrimary = useAsset('ui.menu.btnPrimary');
  const buttonStyle = {
    backgroundImage: `url(${btnPrimary})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  // Toggle button component
  const ToggleButton: React.FC<{
    label: string;
    value: boolean;
    onChange: (value: boolean) => void;
  }> = ({ label, value, onChange }) => (
    <div className="flex items-center justify-between bg-black/40 backdrop-blur-sm rounded-lg px-4 py-3">
      <span className="text-white font-medium text-lg">{label}</span>
      <div className="flex gap-2">
        <button
          onClick={() => onChange(true)}
          className={`px-4 py-1 rounded-md font-bold transition-all ${value
            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
        >
          {t('ui.on', language)}
        </button>
        <button
          onClick={() => onChange(false)}
          className={`px-4 py-1 rounded-md font-bold transition-all ${!value
            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
        >
          {t('ui.off', language)}
        </button>
      </div>
    </div>
  );

  // Slider component
  const VolumeSlider: React.FC<{
    label: string;
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
  }> = ({ label, value, onChange, disabled }) => (
    <div className={`bg-black/40 backdrop-blur-sm rounded-lg px-4 py-3 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white font-medium text-lg">{label}</span>
        <span className="text-yellow-400 font-bold text-lg">{value}%</span>
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        disabled={disabled}
        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:w-5
          [&::-webkit-slider-thumb]:h-5
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-yellow-400
          [&::-webkit-slider-thumb]:shadow-lg
          [&::-webkit-slider-thumb]:shadow-yellow-400/50
          [&::-webkit-slider-thumb]:cursor-pointer
          [&::-webkit-slider-thumb]:transition-transform
          [&::-webkit-slider-thumb]:hover:scale-110"
      />
    </div>
  );

  return (
    <div
      className={`w-full flex flex-col items-center animate-[fadeIn_0.5s_ease-out] ${isMobileShell ? 'min-h-[var(--shell-viewport-height,100vh)] justify-start overflow-y-auto px-4 pb-4' : 'h-screen justify-center'}`}
      style={{
        backgroundImage: `url(${menuBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        paddingTop: isMobileShell ? 'calc(var(--shell-safe-top, 0px) + 64px)' : undefined,
      }}
    >
      <h2
        className={`${isMobileShell ? 'text-3xl mb-4' : 'text-5xl mb-8'} font-extrabold tracking-widest text-white`}
        style={{
          textShadow: '0 0 20px rgba(59,130,246,0.6), 0 0 2px #0b132b, 1px 1px 0 #0b132b, -1px -1px 0 #0b132b',
          WebkitTextStroke: '1.5px #0b132b'
        }}
      >
        {t('ui.optionsTitle', language)}
      </h2>

      {/* Settings Panel */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isMobileShell ? 'gap-4 mt-2' : 'gap-8 mt-6'} w-[800px] max-w-[90vw]`}>
        {/* Left Column */}
        <div className="flex flex-col gap-3">
          {/* Fullscreen Toggle */}
          <button
            onClick={handleFullscreenToggle}
            className="text-white font-bold text-lg py-3 px-6 rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105 flex items-center justify-center gap-3"
            style={buttonStyle}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {isFs ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              )}
            </svg>
            {isFs ? t('ui.exitFullscreen', language) : t('ui.fullscreen', language)}
          </button>

          {/* Volume Controls */}
          <VolumeSlider
            label={t('ui.optionsMusicVolume', language)}
            value={settings.musicVolume}
            onChange={(v) => updateSetting('musicVolume', v)}
          />
          <VolumeSlider
            label={t('ui.optionsSoundVolume', language)}
            value={settings.soundVolume}
            onChange={(v) => updateSetting('soundVolume', v)}
            disabled  // Placeholder - sound system not implemented yet
          />
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-3">
          {/* Toggle Controls */}
          <ToggleButton
            label={t('ui.optionsInstructor', language)}
            value={settings.instructorEnabled}
            onChange={(v) => {
              updateSetting('instructorEnabled', v);
              if (v) {
                // When turning instructor back on, clear the memory of dismissed hints
                // We do this by firing a custom event that InstructorGuide listens to
                window.dispatchEvent(new Event('instructor:resetMemory'));
              }
            }}
          />
          <ToggleButton
            label={t('ui.optionsEffects', language)}
            value={settings.effectsEnabled}
            onChange={(v) => updateSetting('effectsEnabled', v)}
          />
          <ToggleButton
            label={t('ui.optionsTooltips', language)}
            value={settings.tooltipsEnabled}
            onChange={(v) => updateSetting('tooltipsEnabled', v)}
          />
          <ToggleButton
            label={t('ui.optionsActionHints', language)}
            value={settings.actionHintsEnabled}
            onChange={(v) => updateSetting('actionHintsEnabled', v)}
          />
        </div>
      </div>

      {/* Back Button */}
      <div className={`${isMobileShell ? 'w-[240px] mt-4 mb-2' : 'w-[300px] mt-8'}`}>
        <button
          onClick={onBack}
          className="w-full text-slate-300 font-bold text-lg py-3 px-6 rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105"
          style={buttonStyle}
        >
          {t('ui.back', language)}
        </button>
      </div>
    </div>
  );
};

export default OptionsScreen;
